import { Alert } from '@mui/material';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns/drop-down-list';
import { RecurrenceEditorComponent } from '@syncfusion/ej2-react-schedule';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTimePickerComponent } from '@syncfusion/ej2-react-calendars/datetimepicker';
import { CoachAPI } from '../../../api/CoachAPI.js';
import { MEETING_MODE, MEETING_PLATFORM } from './SchedulerConstant.js';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons/index.js';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import CustomPlaceAutocomplete from '../../CustomComponents/CustomPlaceAutocomplete.jsx';
import { SwitchComponent } from '@syncfusion/ej2-react-buttons';
import { isClubOwner, isClubStaff } from '../../../util/UserRoles.js';
import SchedulerWhoSearch from './SchedulerWhoSearch.jsx';
import {
    ALL_MEMBERS_ID,
    SCHEDULE_TYPE_CHOICES,
    allMembersWhoOption,
    canAttachAllSchedulerMembers,
    catalogWhoOptions,
    hydrateSchedulerEvent,
    isOnlineMeeting,
    normalizeRecurrenceRule,
    normalizeTeamIds,
    parseMeetingData,
    playersFromSchedulePayload,
    calendarSlotInterval,
    resolveRsvpMaxSlot,
    resolveWhoOptions,
    typeChoiceForUi,
    uniqueWhoOptions,
} from './schedulerForm.js';

const FIELD_MAPPINGS = {
    event: { value: 'eventId', text: 'eventName' },
    playerTeam: { value: 'id', text: 'name' },
    team: { value: 'teamId', text: 'teamName' },
    court: { text: 'courtNumber', value: 'id' },
    meetingMode: { text: 'mode', value: 'id' },
    meetingPlatform: { text: 'platform', value: 'id' },
};

const RequiredMark = () => <span className="sm-scheduler-required" aria-hidden="true"> *</span>;

const EditorTemplate = ({ args, userInfo, clubId, events, courtData, calenderConfig, players, formError }) => {
    const existing = Boolean(args?.Id);
    const hydrated = hydrateSchedulerEvent(args);
    const meeting = parseMeetingData(args?.MeetingData);
    const canAttachAll = canAttachAllSchedulerMembers(calenderConfig, userInfo);
    const selectedTeamIds = normalizeTeamIds(args?.TeamIds);

    const [scheduleType, setScheduleType] = useState(args?.ScheduleType ?? 'TEAM_PRACTICE');
    const [homeTeams, setHomeTeams] = useState([]);
    const [clubTeams, setClubTeams] = useState([]);
    const [meetingMode, setMeetingMode] = useState(
        meeting.mode || (args?.ScheduleType === 'MEETING' ? 'OFFLINE' : '')
    );
    const [teamPlayers, setTeamPlayer] = useState([]);
    const [isRSVP, setIsRSVP] = useState(hydrated.IsRSVP);
    const [isAttachAllMember, setIsAttachAllMember] = useState(
        existing ? hydrated.IsAttachAllMember : canAttachAll
    );
    const [whoIds, setWhoIds] = useState(() => (
        existing && !hydrated.IsAttachAllMember ? selectedTeamIds : []
    ));
    const [whoOptions, setWhoOptions] = useState(() => uniqueWhoOptions([
        ...(canAttachAll ? [allMembersWhoOption()] : []),
        ...resolveWhoOptions(existing && !hydrated.IsAttachAllMember ? selectedTeamIds : [], args?.TeamIds, players),
    ]));
    const [includeGuests, setIncludeGuests] = useState(hydrated.IsAttachAllGuest);
    const [venue, setVenue] = useState(args?.Venue ?? '');
    const [description, setDescription] = useState(args?.Description ?? '');
    const recObject = useRef(null);
    const modeRef = useRef(null);
    const platformRef = useRef(null);
    const titleRef = useRef(null);
    const isAttachAllGuestRef = useRef(null);

    const privacyRestricted = Boolean(
        calenderConfig?.coachPrivacy && !isClubOwner(userInfo) && !isClubStaff(userInfo)
    );
    const playerSource = privacyRestricted ? teamPlayers : playersFromSchedulePayload(players);
    const whoCatalog = useMemo(
        () => catalogWhoOptions(playerSource, clubTeams),
        [playerSource, clubTeams],
    );
    const hasEvents = Boolean(events?.length);
    const onlineMeeting = isOnlineMeeting(scheduleType, meetingMode);
    const showCourt = false;
    const typeForUi = typeChoiceForUi(scheduleType);

    const getAllUsersForAClub = async () => {
        try {
            const response = await CoachAPI.findAllTeamPlayerForCoach(clubId, userInfo.userId);
            setTeamPlayer(response.Data);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (privacyRestricted) {
            getAllUsersForAClub();
        }
    }, []);

    useEffect(() => {
        if (!clubId) {
            return;
        }
        let cancelled = false;
        CoachAPI.findAllTeamByClubId(clubId)
            .then((res) => {
                if (!cancelled) {
                    const teams = res?.Data ?? [];
                    setClubTeams(teams);
                    setHomeTeams(teams);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setClubTeams([]);
                    setHomeTeams([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [clubId]);

    const handleTypeChange = (type) => {
        if (existing) {
            return;
        }
        setScheduleType(type);
        if (type === 'MEETING' && !meetingMode) {
            setMeetingMode('OFFLINE');
        }
    };

    useEffect(() => {
        setWhoOptions((prev) => uniqueWhoOptions([
            ...prev,
            ...resolveWhoOptions(whoIds, whoCatalog, args?.TeamIds, prev),
        ]));
    }, [whoCatalog]);

    const pickWho = (item) => {
        if (item?.id === ALL_MEMBERS_ID) {
            setIsAttachAllMember(true);
            setWhoIds([]);
            setWhoOptions([allMembersWhoOption()]);
            return;
        }
        setIsAttachAllMember(false);
        setWhoOptions((prev) => uniqueWhoOptions([...prev.filter((row) => row.id !== ALL_MEMBERS_ID), item]));
        setWhoIds((prev) => normalizeTeamIds([...prev, item.id]));
    };

    const removeWho = (id) => {
        if (String(id) === String(userInfo?.userId)) {
            return;
        }
        if (id === ALL_MEMBERS_ID) {
            setIsAttachAllMember(false);
            setWhoIds([]);
            setWhoOptions([]);
            return;
        }
        setWhoIds((prev) => prev.filter((value) => String(value) !== String(id)));
        setWhoOptions((prev) => prev.filter((row) => String(row.id) !== String(id)));
    };

    useEffect(() => {
        if (titleRef.current && args?.Subject) {
            titleRef.current.value = args.Subject;
        }
        if (isAttachAllGuestRef.current) {
            isAttachAllGuestRef.current.checked = hydrated.IsAttachAllGuest;
        }
        if (modeRef.current && meeting.mode) {
            modeRef.current.value = meeting.mode;
        }
        if (platformRef.current && meeting.platform) {
            platformRef.current.value = meeting.platform;
        }
    }, []);

    const renderCourtItem = (data) => (
        <div className="sm-scheduler-court-option">
            <span className="sm-scheduler-court-swatch" style={{ background: data?.color || '#3f51b5' }} />
            <span>{data?.courtNumber}</span>
        </div>
    );

    return (
        <table className='sm-custom-event-editor'>
            <tbody className='sm-custom-event-editor-tbody'>
                {formError &&
                    <Alert severity='error'>
                        {formError}
                    </Alert>}
                <tr>
                    <td colSpan={2} className='sm_coach_scheduler_editor_templete_sec_5'>
                        <TextBoxComponent ref={titleRef} placeholder='Title *' id='title' floatLabelType='Auto' value={args?.Subject} />
                    </td>
                </tr>
                <tr className="sm-scheduler-type-rsvp-row">
                    <td>
                        <div className="sm-scheduler-type-field">
                            <p className="sm-scheduler-field-label">Type</p>
                            <div className="sm-scheduler-type-pills" role="group" aria-label="Schedule type">
                                {SCHEDULE_TYPE_CHOICES.map((choice) => (
                                    <button
                                        type="button"
                                        key={choice.id}
                                        disabled={existing}
                                        className={typeForUi === choice.id ? 'is-active' : ''}
                                        onClick={() => handleTypeChange(choice.id)}
                                    >
                                        {choice.label}
                                    </button>
                                ))}
                            </div>
                            <input type="hidden" id="schedule_type" value={scheduleType} readOnly />
                        </div>
                    </td>
                    <td>
                        <label className="sm-scheduler-rsvp-row" htmlFor="is_rsvp">
                            <input
                                type="checkbox"
                                id="is_rsvp"
                                className="e-field"
                                name="is_rsvp"
                                checked={isRSVP}
                                onChange={(e) => setIsRSVP(e.target.checked)}
                            />
                            <span>RSVP</span>
                            <input type="hidden" id="maxSlot" value={resolveRsvpMaxSlot(isRSVP, args?.MaxSlot)} readOnly />
                        </label>
                    </td>
                </tr>
                <tr className='sm_coach_scheduler_editor_templete_sec_7'>
                    <td>
                        <DateTimePickerComponent value={args?.StartTime} floatLabelType="Auto" data-testid="dateTimePickerFrom" placeholder='From *' className='e-field' id='StartTime' data-name='StartTime' step={calendarSlotInterval(calenderConfig)}>
                        </DateTimePickerComponent>
                    </td>
                    <td>
                        <DateTimePickerComponent value={args?.EndTime} floatLabelType="Auto" data-testid="dateTimePickerFrom" placeholder='To *' className='e-field' id='EndTime' data-name='EndTime' step={calendarSlotInterval(calenderConfig)}>
                        </DateTimePickerComponent>
                    </td>
                </tr>
                <tr className="sm-scheduler-who-row">
                    <td colSpan={2}>
                        <div className="sm-scheduler-who-field">
                            <p className="sm-scheduler-field-label">Who<RequiredMark /></p>
                            <input
                                type="checkbox"
                                id="is_attach_all_member"
                                checked={isAttachAllMember}
                                readOnly
                                hidden
                            />
                            <input type="hidden" id="PlayerAndTeamList" value={JSON.stringify(whoOptions.filter((item) => item.id !== ALL_MEMBERS_ID))} readOnly />
                            <input type="hidden" id="PlayerAndTeam" value={JSON.stringify(whoIds)} readOnly />
                            <div className="sm-who-chip-row">
                                {isAttachAllMember &&
                                    <span className="sm-who-chip">
                                        All members
                                        <button type="button" aria-label="Clear all members" onClick={() => removeWho(ALL_MEMBERS_ID)}>×</button>
                                    </span>}
                                {!isAttachAllMember && whoOptions.filter((item) => item.id !== ALL_MEMBERS_ID).map((item) =>
                                    <span className="sm-who-chip" key={item.id}>
                                        {item.name}
                                        <button type="button" aria-label={`Remove ${item.name}`} onClick={() => removeWho(item.id)}>×</button>
                                    </span>)}
                            </div>
                            <SchedulerWhoSearch
                                clubId={clubId}
                                catalog={whoCatalog}
                                selected={whoOptions}
                                canAttachAll={canAttachAll}
                                onPick={pickWho}
                            />
                        </div>
                    </td>
                </tr>
                {isAttachAllMember &&
                    <tr className="sm-scheduler-guest-row">
                        <td colSpan={2}>
                            <CheckBoxComponent
                                ref={isAttachAllGuestRef}
                                id='is_attach_guest'
                                checked={includeGuests}
                                onChange={(e) => setIncludeGuests(e.target.checked)}
                                label='Include guests'
                            />
                        </td>
                    </tr>}
                <tr data-testid="recurrence-editor-container" className='content-wrapper recurrence-editor-wrap sm_coach_scheduler_editor_templete_sec_5'>
                        <td className='RecurrenceEditor1'>
                            <p className="sm-scheduler-field-label">Repeat</p>
                            <input type="hidden" id="RecurrenceRule" defaultValue={normalizeRecurrenceRule(args?.RecurrenceRule) || ''} />
                            <RecurrenceEditorComponent
                                frequencies={['none', 'daily', 'weekly', 'monthly']}
                                endTypes={['until', 'count']}
                                value={args?.RecurrenceRule || null}
                                startDate={args?.StartTime}
                                id="coach-recurrence-editor"
                                data-testid="recurrence-editor"
                                ref={recObject}
                                change={(e) => {
                                    const el = document.querySelector('#RecurrenceRule');
                                    if (el) {
                                        el.value = normalizeRecurrenceRule(e.value) || '';
                                    }
                                }}
                            />
                        </td>
                    </tr>
                {(hasEvents && scheduleType !== 'MEETING' || showCourt || scheduleType === 'MEETING') &&
                    <tr className='sm_coach_scheduler_editor_templete_sec_2'>
                        {hasEvents && scheduleType !== 'MEETING' &&
                            <td>
                                <DropDownListComponent floatLabelType="Auto"
                                    isDeviceFullScreen={false}
                                    allowFiltering={false}
                                    className='e-field'
                                    id='Event'
                                    showClearButton='false'
                                    fields={FIELD_MAPPINGS.event}
                                    dataSource={events}
                                    value={args?.EventId}
                                    placeholder='Event' />
                            </td>}
                        {scheduleType === 'MEETING' &&
                            <td>
                                <DropDownListComponent ref={modeRef} floatLabelType="Auto"
                                    isDeviceFullScreen={false}
                                    allowFiltering={false}
                                    showClearButton='false'
                                    className='e-field'
                                    id='MeetingMode'
                                    fields={FIELD_MAPPINGS.meetingMode}
                                    value={meetingMode || 'OFFLINE'}
                                    onChange={(e) => setMeetingMode(e.target.value)}
                                    dataSource={MEETING_MODE}
                                    placeholder='Meeting mode *' />
                            </td>}
                        {showCourt &&
                            <td>
                                <DropDownListComponent floatLabelType="Auto"
                                    allowFiltering={false}
                                    className='e-field'
                                    id='CourtNumber'
                                    fields={FIELD_MAPPINGS.court}
                                    value={args?.CourtNumber}
                                    dataSource={courtData}
                                    placeholder='Court'
                                    itemTemplate={renderCourtItem}
                                    valueTemplate={renderCourtItem}
                                />
                            </td>}
                    </tr>}
                {scheduleType === 'EVENT' &&
                    <tr className='sm_coach_scheduler_editor_templete_sec_4'>
                        <td>
                            <TextBoxComponent value={args?.AwayOrganizationName} floatLabelType='Auto' placeholder="Away Organization" id='AwayOrganizationText' />
                        </td>
                    </tr>}
                {scheduleType === 'EVENT' &&
                    <tr className='sm_coach_scheduler_editor_templete_sec_2'>
                        <td>
                            <DropDownListComponent allowFiltering={true} floatLabelType="Auto" value={args?.HomeTeamId} fields={FIELD_MAPPINGS.team} dataSource={homeTeams} className='e-field' id='home_team' placeholder='Home Team' />
                        </td>
                        <td>
                            <TextBoxComponent value={args?.AwayTeamName} floatLabelType='Auto' placeholder="Away Team Name" id='AwayTeamNameText' />
                        </td>
                    </tr>}
                <tr className='sm_coach_scheduler_editor_templete_sec_5'>
                    <td colSpan={2}>
                        {onlineMeeting ?
                            <DropDownListComponent ref={platformRef} floatLabelType="Auto"
                                allowFiltering={false}
                                className='e-field'
                                id='Platform'
                                fields={FIELD_MAPPINGS.meetingPlatform}
                                dataSource={MEETING_PLATFORM}
                                value={meeting.platform}
                                placeholder='Meeting platform *' />
                            :
                            <CustomPlaceAutocomplete
                                label='Location'
                                value={venue}
                                id="Venue"
                                isRequired={true}
                                onChange={setVenue}
                            />}
                    </td>
                </tr>
                <tr className="sm-scheduler-description-row">
                    <td colSpan={2}>
                        <label className="sm-scheduler-description-field" htmlFor="Description">
                            <span className="sm-scheduler-field-label">Description</span>
                            <textarea
                                id="Description"
                                placeholder="Description"
                                className="e-field e-input custom-border"
                                name="Description"
                                rows={3}
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                            />
                        </label>
                    </td>
                </tr>
                <tr>
                    <td colSpan={2} className="sm-scheduler-notify-row">
                        <p>Send notification</p>
                        <SwitchComponent id='isEmailDispatch' />
                    </td>
                </tr>
            </tbody>
        </table>
    );
};

export default EditorTemplate
